import { Routes, Route, Navigate } from 'react-router-dom';
import { RouteGuard } from './components/auth/RouteGuard';
import PersistentMainLayout from './components/layout/PersistentMainLayout';
import { User } from './types';
import { ModalProvider } from './contexts/ModalContext';

// Auth Components
import SignUp from './components/auth/forms/SignUpForm';
import LoginForm from './components/auth/forms/LoginForm';

// Page Components
import Dashboard from './pages/dashboard';
import Groups from './pages/groups';
import Users from './pages/users';
import ResourceRelationshipsPage from './pages/resource-relationships';
import OrganizationsPage from './pages/organizations/system';

// Support Components
import SupportPage from './pages/support';

// RAG Application Components
import RAGApplications from './pages/rag-applications';
import CreateRAG from './pages/rag-applications/create';
import EditRAG from './pages/rag-applications/[id]/edit';

// Knowledge Base Components
import KnowledgeBase from './pages/knowledge-bases';
import CreateKnowledgeBase from './pages/knowledge-bases/create';
import EditKnowledgeBase from './pages/knowledge-bases/[id]/edit';

// Source Components
import UploadedDriveFiles from './pages/data-sources/uploaded';
import Files from './pages/data-sources/files';
import DriveFiles from './pages/data-sources/drive';

// Web Source Components
import WebSources from './pages/data-sources/web-sources';
import CreateWebSource from './pages/data-sources/web-sources/create';
import EditWebSource from './pages/data-sources/web-sources/[id]/edit';

// API Source Components
import APISources from './pages/data-sources/api-sources';
import CreateAPISource from './pages/data-sources/api-sources/create';
import EditAPISource from './pages/data-sources/api-sources/[id]/edit';

// Chat Components
import ChatPage from './pages/chat/[id]/index';

// Billing Components
import BillingPage from './pages/organizations/settings/billing/subscription';
import PaymentMethodPage from './pages/organizations/settings/billing/payment';
import UsagePage from './pages/organizations/settings/billing/usage';

const AppRoutes = () => {
    return (
        <ModalProvider>
            <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginForm />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/unauthorized" element={
                    <div className="min-vh-100 d-flex align-items-center justify-content-center">
                        <div className="text-center">
                            <h1>Unauthorized Access</h1>
                            <p>You don't have permission to access this resource.</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => window.location.href = '/login'}
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                } />

                {/* Protected Routes */}
                <Route element={
                    <RouteGuard>
                        <PersistentMainLayout />
                    </RouteGuard>
                }>
                    {/* Root redirect */}
                    <Route path="/" element={
                        <RouteGuard>
                            {({ user }: { user: User }) => {
                                // Add detailed logging
                                console.log('Root route redirect execution', {
                                    userRole: user?.role,
                                    userInfo: user
                                });

                                // Wait for role information to be available
                                if (!user.role) {
                                    console.log('Root route - waiting for role information');
                                    return <div>Loading...</div>;
                                }

                                let defaultRoute;
                                if (user.role === 'system_admin') {
                                    defaultRoute = '/organizations';
                                } else if (user.role === 'user') {
                                    defaultRoute = '/rag-applications';
                                } else {
                                    defaultRoute = '/dashboard';
                                }

                                console.log('Root route - redirecting to', {
                                    userRole: user.role,
                                    redirectTo: defaultRoute
                                });

                                return <Navigate to={defaultRoute} replace />;
                            }}
                        </RouteGuard>
                    } />

                    {/* Support Routes */}
                    <Route path="/support" element={
                        <RouteGuard roles={['admin', 'user', 'rag_user'] as const}>
                            <SupportPage />
                        </RouteGuard>
                    } />

                    {/* System Admin Routes */}
                    <Route path="/organizations" element={
                        <RouteGuard roles={['system_admin'] as const}>
                            <OrganizationsPage />
                        </RouteGuard>
                    } />

                    {/* Admin Routes */}
                    <Route path="/dashboard" element={
                        <RouteGuard roles={['admin', 'user'] as const}>
                            <Dashboard />
                        </RouteGuard>
                    } />

                    {/* Directory Routes */}
                    <Route path="/directory/groups" element={
                        <RouteGuard roles={['admin'] as const}>
                            <Groups />
                        </RouteGuard>
                    } />
                    <Route path="/directory/users" element={
                        <RouteGuard roles={['admin'] as const}>
                            <Users />
                        </RouteGuard>
                    } />

                    {/* Resource Routes */}
                    <Route path="/resources" element={
                        <RouteGuard roles={['admin'] as const}>
                            <ResourceRelationshipsPage />
                        </RouteGuard>
                    } />

                    {/* RAG Application Routes */}
                    <Route path="/rag-applications" element={
                        <RouteGuard roles={['admin', 'user', 'rag_user'] as const}>
                            <RAGApplications />
                        </RouteGuard>
                    } />
                    <Route path="/rag-applications/create" element={
                        <RouteGuard roles={['admin', 'user'] as const}>
                            <CreateRAG />
                        </RouteGuard>
                    } />
                    <Route path="/rag-applications/:id/edit" element={
                        <RouteGuard roles={['admin', 'user'] as const}>
                            <EditRAG />
                        </RouteGuard>
                    } />

                    {/* Knowledge Base Routes */}
                    <Route path="/knowledge-bases" element={
                        <RouteGuard roles={['admin', 'user'] as const}>
                            <KnowledgeBase />
                        </RouteGuard>
                    } />
                    <Route path="/knowledge-bases/create" element={
                        <RouteGuard roles={['admin', 'user'] as const}>
                            <CreateKnowledgeBase />
                        </RouteGuard>
                    } />
                    <Route path="/knowledge-bases/:id/edit" element={
                        <RouteGuard roles={['admin', 'user'] as const}>
                            <EditKnowledgeBase />
                        </RouteGuard>
                    } />

                    {/* Data Source Routes */}
                    <Route path="/data-sources/uploaded" element={
                        <RouteGuard roles={['admin', 'user'] as const}>
                            <UploadedDriveFiles />
                        </RouteGuard>
                    } />
                    <Route path="/data-sources" element={
                        <RouteGuard roles={['admin', 'user'] as const}>
                            <Files />
                        </RouteGuard>
                    } />
                    <Route path="/data-sources/drive" element={
                        <RouteGuard roles={['admin'] as const}>
                            <DriveFiles />
                        </RouteGuard>
                    } />

                    {/* Web Sources Routes */}
                    <Route path="/data-sources/web" element={
                        <RouteGuard roles={['admin'] as const}>
                            <WebSources />
                        </RouteGuard>
                    } />
                    <Route path="/data-sources/web/create" element={
                        <RouteGuard roles={['admin'] as const}>
                            <CreateWebSource />
                        </RouteGuard>
                    } />
                    <Route path="/data-sources/web/:id/edit" element={
                        <RouteGuard roles={['admin'] as const}>
                            <EditWebSource />
                        </RouteGuard>
                    } />

                    {/* API Sources Routes */}
                    <Route path="/data-sources/api" element={
                        <RouteGuard roles={['admin'] as const}>
                            <APISources />
                        </RouteGuard>
                    } />
                    <Route path="/data-sources/api/create" element={
                        <RouteGuard roles={['admin'] as const}>
                            <CreateAPISource />
                        </RouteGuard>
                    } />
                    <Route path="/data-sources/api/:id/edit" element={
                        <RouteGuard roles={['admin'] as const}>
                            <EditAPISource />
                        </RouteGuard>
                    } />

                    {/* Chat Routes */}
                    <Route path="/chat/:id" element={
                        <RouteGuard roles={['admin', 'user', 'rag_user'] as const}>
                            <ChatPage />
                        </RouteGuard>
                    } />

                    {/* Billing Routes */}
                    <Route path="/organization/settings/billing" element={
                        <RouteGuard roles={['admin'] as const}>
                            <BillingPage />
                        </RouteGuard>
                    } />
                    <Route path="/organization/settings/billing/payment" element={
                        <RouteGuard roles={['admin'] as const}>
                            <PaymentMethodPage />
                        </RouteGuard>
                    } />
                    <Route path="/analytics/usage" element={
                        <RouteGuard roles={['admin'] as const}>
                            <UsagePage />
                        </RouteGuard>
                    } />
                </Route>
            </Routes>
        </ModalProvider>
    );
};

export default AppRoutes;